/**
 * Hooks for Code Browser feature using simple fetch + state
 */

import { useState, useEffect, useCallback } from 'react'
import { fetchFileTree, fetchFileContent } from '@/lib/code-api'
import type { FileNode, FileContent } from '@/types/code'

interface UseFileTreeResult {
  data: { tree: FileNode[] } | null
  isLoading: boolean
  error: Error | null
  isRefetching: boolean
  refetch: () => void
}

/**
 * Hook to fetch the file tree for a project
 */
export function useFileTree(projectId: number, enabled = true): UseFileTreeResult {
  const [data, setData] = useState<{ tree: FileNode[] } | null>(null)
  const [isLoading, setIsLoading] = useState(enabled)
  const [isRefetching, setIsRefetching] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const fetchData = useCallback(async (isRefetch = false) => {
    if (!enabled) return

    if (isRefetch) {
      setIsRefetching(true)
    } else {
      setIsLoading(true)
    }
    setError(null)

    try {
      const result = await fetchFileTree(projectId)
      setData(result)
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch file tree'))
    } finally {
      setIsLoading(false)
      setIsRefetching(false)
    }
  }, [projectId, enabled])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const refetch = useCallback(() => {
    fetchData(true)
  }, [fetchData])

  return { data, isLoading, error, isRefetching, refetch }
}

interface UseFileContentResult {
  data: FileContent | null
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to fetch a file's content
 */
export function useFileContent(projectId: number, path: string | null): UseFileContentResult {
  const [data, setData] = useState<FileContent | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!path) {
      setData(null)
      setError(null)
      return
    }

    let cancelled = false

    const fetchData = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const result = await fetchFileContent(projectId, path)
        if (!cancelled) {
          setData(result)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error('Failed to fetch file content'))
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchData()

    return () => {
      cancelled = true
    }
  }, [projectId, path])

  return { data, isLoading, error }
}
